/** The addition a planner signed off, as the grid holds it. */
export interface SuggestedAddition {
  quantity: number;
  unitCost: number;
  totalCost: number;
}

/** The line that addition joins, when the order already carries the SKU. A null
 * quantity means the view was never told what the order holds, which is not the
 * same as an order holding none of it. */
export interface JoinedLine {
  quantity?: number | null;
  unitCost?: number | null;
  placedDate?: string | null;
  deliveryDate?: string | null;
}

export interface MergedLine {
  quantity: number;
  unitCost: number;
  totalCost: number;
  /** Null when the addition stands as its own line and keeps its own dates. */
  placedDate: string | null;
  deliveryDate: string | null;
}

const round2 = (n: number): number => Math.round(n * 100) / 100;
// Enough places that quantity x price still reconciles to the amount on the
// document, few enough that binary floating point noise never reaches a sheet.
const round6 = (n: number): number => Math.round(n * 1e6) / 1e6;

/**
 * What a signed-off change to a placed order is saved as: the joined line's new
 * total, on that line's own dates, so the vendor's document shows one line for
 * the SKU rather than two. An addition the order has no line for stands alone.
 */
export const mergeSuggestedChange = (
  addition: SuggestedAddition,
  line: JoinedLine,
): MergedLine => {
  const onPo = line.quantity != null ? Number(line.quantity) : 0;
  if (!(onPo > 0)) {
    return {
      quantity: addition.quantity,
      unitCost: addition.unitCost,
      totalCost: addition.totalCost,
      placedDate: null,
      deliveryDate: null,
    };
  }
  // Each half keeps the price it was agreed at, so the money is preserved and
  // the merged line's unit cost is simply what it now averages to. An unknown
  // price on the joined line falls back to the addition's rather than to free.
  // The average keeps more than cents: rounded to two places it would make
  // quantity x price disagree with the amount on the vendor's own document.
  const quantity = onPo + addition.quantity;
  const totalCost =
    onPo * Number(line.unitCost ?? addition.unitCost) + addition.totalCost;
  return {
    quantity,
    totalCost: round2(totalCost),
    unitCost: quantity > 0 ? round6(totalCost / quantity) : addition.unitCost,
    placedDate: line.placedDate || null,
    deliveryDate: line.deliveryDate || null,
  };
};
